import type { ReactNode } from 'react';
import { Badge, Box, InlineStack, Link, Stack } from '@customerio/pluma-components/react';
import { AttributeTypeLabel, type IdentifiersConfig } from '@/features/people';
import { AutogeneratedAttributeTooltip } from './autogenerated-attribute-tooltip';
import { RowClickGuard } from './row-click-guard';
import * as styles from './name-cell.css';

/**
 * A row's name, what is known about it, and its description.
 *
 * The name opens the row's details beside the list, as a button, the way
 * the page this replaces draws it; the drawer's "View full page" is the way
 * to the item's own page. The description sits
 * under it and is edited in place, because it is the one thing about a row
 * that the workspace writes rather than the ingestion.
 */

export type NameCellProps = {
  name: string;
  /** What the name reads, which for an event keeps its ingested casing. */
  displayName: string;
  isSensitive: boolean;
  isAutogenerated: boolean;
  /** Only person attributes carry a Reserved or Identifier badge. */
  identifiers: IdentifiersConfig | null | undefined;
  /** Whether the workspace identifies people by more than one attribute. */
  isMultiIdentifier: boolean;
  /** The workspace whose identifier settings the badge's tooltip links to. */
  workspaceId: number;
  withAttributeBadges: boolean;
  /** The description, which the row's own editor renders. */
  description: ReactNode;
  /** Opens this row's details beside the list. */
  onOpen: () => void;
};

export function NameCell({
  name,
  displayName,
  isSensitive,
  isAutogenerated,
  identifiers,
  isMultiIdentifier,
  workspaceId,
  withAttributeBadges,
  description,
  onOpen,
}: NameCellProps) {
  return (
    <Stack>
      <InlineStack alignBlock="center" gap="075">
        <RowClickGuard>
          {/* A button styled as a link, as the page this replaces draws the
              name (`name-cell/index.gts`, a `PlumaLink` with `@onClick` and
              no `@href`): it opens the details beside the list, and the
              drawer's "View full page" is the way to the item's own page. */}
          <Link variant="primary" truncate onClick={onOpen}>
            {displayName}
          </Link>
        </RowClickGuard>
        {isAutogenerated ? <AutogeneratedAttributeTooltip attribute={name} /> : null}
        {withAttributeBadges ? (
          <AttributeTypeLabel
            attribute={name}
            identifiers={identifiers}
            identifierBadge={{ isMultiIdentifier, workspaceId: String(workspaceId) }}
          />
        ) : null}
        {isSensitive ? <Badge color="blue">Sensitive</Badge> : null}
      </InlineStack>
      {/* Nothing between the two lines: the page this replaces stacks the
          name and the description in one rhythm, and 4px here made every
          row 63px against its 59px (`name-cell/index.gts:215`). */}
      <Box className={styles.description}>{description}</Box>
    </Stack>
  );
}
